/**
 * @file PrivateRoute.jsx
 * @description Sicherheits-Wächter (Route Guard) für geschützte Bereiche in ThekenOS.
 *
 * Wie die Schutzlogik funktioniert:
 * 1. **Nicht eingeloggt**: Leitet sofort zum Login-Bildschirm (`/login`) weiter.
 * 2. **Rolle wird noch geladen**: Zeigt einen dezenten Ladeindikator ("Lade Berechtigungen…"),
 *    um Fehlumleitungen während des Verbindungsaufbaus zu verhindern.
 * 3. **Keine Rolle zugewiesen**: Zeigt eine Sperrseite mit Kontakt-Hinweis zum Administrator.
 * 4. **Rollenprüfung**: Besitzt der Nutzer nicht die für diese Seite erlaubte Rolle
 *    (z. B. Theki versucht Benutzerverwaltung aufzurufen), wird er zurück zum Dashboard geleitet.
 * 5. **Zusatzberechtigung**: Seiten, deren Zugang nicht allein von der Rolle abhängt, geben
 *    über `allow` eine Prüffunktion mit (z. B. `canAccessEinkauf`). So teilen sich Navigation
 *    und Routen-Wächter dieselbe Regel, statt sie doppelt zu pflegen.
 * 6. **Erlaubt**: Rendert die angeforderten Kind-Elemente (`children`).
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { recordRedirect, isRedirectLoopTripped } from '../utils/redirectBreaker';
import RedirectLoopFallback from './RedirectLoopFallback';

/**
 * Kapselt geschützte Seiten und setzt die Berechtigungsregeln durch.
 *
 * @param {object} props - Komponenten-Properties
 * @param {React.ReactNode} props.children - Die geschützte Seite/Komponente
 * @param {string[]} [props.allowedRoles] - Optional: Array von Rollen, die Zugriff haben (z. B. `['Admin', 'System-Admin']`)
 * @param {(role: string|null, permissions: object) => boolean} [props.allow] - Optional: Zusätzliche
 *   Prüffunktion für Berechtigungen jenseits der reinen Rolle. Muss `true` liefern, damit die
 *   Seite gerendert wird; wird zusätzlich zu `allowedRoles` ausgewertet.
 * @returns {JSX.Element} Die Seite oder eine automatische Weiterleitung
 */
export default function PrivateRoute({ children, allowedRoles, allow }) {
  const { user, role, roleLoading, permissions } = useAuth();
  const location = useLocation();

  // 1. Nicht angemeldet -> Notbremse prüfen und idempotent zum Login weiterleiten
  if (!user) {
    if (isRedirectLoopTripped()) {
      return <RedirectLoopFallback />;
    }

    // Loop-Guard: Befinden wir uns bereits auf /login, keine weitere Weiterleitung auslösen
    if (location.pathname === '/login') {
      return null;
    }

    const currentPath = location.pathname + (location.search || '');
    const searchParams = new URLSearchParams();

    // Zielpfad idempotent mit .set() hinterlegen (nur für sinnvolle Rücksprungziele)
    if (currentPath && currentPath !== '/' && !currentPath.startsWith('/login')) {
      searchParams.set('redirect', currentPath);
    }

    const query = searchParams.toString();
    const loginTarget = query ? `/login?${query}` : '/login';

    const { tripped } = recordRedirect();
    if (tripped) {
      return <RedirectLoopFallback />;
    }

    return <Navigate to={loginTarget} replace />;
  }

  // 2. Rolle wird noch asynchron aus der Datenbank abgefragt -> Warten statt fälschlich abweisen
  if (roleLoading) {
    return <div className="global-loading-container">Lade Berechtigungen…</div>;
  }

  // 3. Benutzer ist angemeldet, hat aber noch keine Rolle zugewiesen bekommen
  if (!role || role === 'Keine Rolle') {
    return (
      <div className="global-loading-container" style={{ flexDirection: 'column', gap: '1rem', textAlign: 'center' }}>
        <h2>Zugriff verweigert</h2>
        <p>Du hast noch keine Rolle zugewiesen bekommen.<br/>Bitte wende dich an einen System-Administrator.</p>
      </div>
    );
  }

  // 4. Prüfen, ob die Rolle des Nutzers in der Liste der erlaubten Rollen enthalten ist
  if (allowedRoles && Array.isArray(allowedRoles) && !allowedRoles.includes(role)) {
    if (location.pathname === '/dashboard') {
      return null;
    }
    const { tripped } = recordRedirect();
    if (tripped) {
      return <RedirectLoopFallback />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  // 5. Zusatzberechtigung prüfen (z. B. Einkaufsrecht eines Redakteurs)
  if (typeof allow === 'function' && !allow(role, permissions)) {
    if (location.pathname === '/dashboard') {
      return null;
    }
    const { tripped } = recordRedirect();
    if (tripped) {
      return <RedirectLoopFallback />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  // 6. Alles in Ordnung -> Seite anzeigen
  return children;
}

